/**
 * FarmList Page
 * 
 * This page displays a list of farms based on the selected category.
 * It fetches farms by proximity to the user's location and allows toggling between a list view
 * and a map view. The page also handles responsive design for mobile devices.
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FarmListPageStyle from "../../assets/stylesheets/farm-list/FarmListPage.module.css";
import HeaderStyle from "../../assets/stylesheets/Header.module.css";
import { findFarmsByProximity } from "../../api/search-service/searchService"
import { getCategoryById } from "../../api/farm-service/categoryService";
import { useMapView } from "../../hooks/MapViewContext.js";
import useUserLocation from "../../hooks/useUserLocation";
import ListGoogleMapComponent from "../../components/common/map/ListGoogleMap.jsx";
import FarmCardList from "../../components/farm/FarmCardList";
import Pagination from "../../components/common/Pagination";
import Logger from "../../utils/logger";
import slugify from 'slugify';

const log = Logger.create('FarmList');

const COORDINATE_TOLERANCE = 0.000001; // Tolerance for duplicate searches
const VERBOSE = false;
const DEFAULT_PROXIMITY_DISTANCE = 10000; // 10,000 km radius for proximity search
const PAGE_SIZE = 12;
const API_PAGE_LIMIT = 50;
const SORT = 'distance'; 
const ORDER = 'ASC';

const toFiniteNumber = (value) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const filterFarmsWithinBounds = (farmList, bounds) => {
  if (!Array.isArray(farmList)) {
    return [];
  }

  if (!bounds) {
    return farmList;
  }

  const north = toFiniteNumber(bounds.north);
  const south = toFiniteNumber(bounds.south);
  const east = toFiniteNumber(bounds.east);
  const west = toFiniteNumber(bounds.west);

  if ([north, south, east, west].some((coordinate) => coordinate === null)) {
    return farmList;
  }

  const crossesDateline = west > east;

  return farmList.filter((farm) => {
    const lat = toFiniteNumber(farm?.location?.coordinates?.lat);
    const lng = toFiniteNumber(farm?.location?.coordinates?.lon);

    if (lat === null || lng === null) {
      return false;
    }

    const withinLat = lat <= north && lat >= south;
    const withinLng = crossesDateline
      ? lng >= west || lng <= east
      : lng >= west && lng <= east;

    return withinLat && withinLng;
  });
};

const calculatePaginationMeta = (metaSource, visibleCount, limit, currentPageOverride = null) => {
  const baseMeta = metaSource && typeof metaSource === 'object' ? { ...metaSource } : {};
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 1;

  baseMeta.total = visibleCount;
  baseMeta.pages = visibleCount > 0 ? Math.ceil(visibleCount / safeLimit) : 1;
  baseMeta.limit = safeLimit;
  const desiredPage = currentPageOverride ?? baseMeta.page ?? 1;
  baseMeta.page = Math.min(desiredPage, baseMeta.pages);

  return baseMeta;
};

const paginateFarms = (farmList, page, pageSize) => {
  if (!Array.isArray(farmList) || pageSize <= 0) {
    return [];
  }

  const safePage = Math.max(1, Number.isFinite(page) ? page : 1);
  const startIndex = (safePage - 1) * pageSize;
  if (startIndex >= farmList.length) {
    return [];
  }

  return farmList.slice(startIndex, startIndex + pageSize);
};

/**
 * FarmList component displays a list of farms based on the selected category.
 * It fetches farms by proximity to the user's location and allows toggling between a list view and a map view.
 * @returns {JSX.Element} The rendered component.
 * 
 */
export default function FarmList() {
  const navigate = useNavigate();
  const locationObj = useLocation();
  const { categoryId } = useParams();
  const { showMap, setShowMap } = useMapView();

  const [category, setCategory] = useState([]);
  const [farms, setFarms] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({});
  const { location, error: locationError, loading: locationLoading } = useUserLocation();
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 787);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [isMapSearch, setIsMapSearch] = useState(false);
  const [lastSearchLocation, setLastSearchLocation] = useState(null); // Track last search
  const [hoveredFarmId, setHoveredFarmId] = useState(null);
  const [animClass, setAnimClass] = useState("");
  const [mobileNavHeight, setMobileNavHeight] = useState(75); 
  const allFarmsRef = React.useRef([]);
  const mapBoundsRef = React.useRef(null);
  const filteredFarmsRef = React.useRef([]);
  const lastSearchCategoryRef = React.useRef(null);

  const refreshVisibleFarms = useCallback((farmList, bounds, options = {}) => {
    const { metaOverride = null, pageOverride = 1 } = options;
    const filtered = filterFarmsWithinBounds(farmList, bounds);
    filteredFarmsRef.current = filtered;

    const totalPages = filtered.length > 0 ? Math.ceil(filtered.length / PAGE_SIZE) : 1;
    const safePage = Math.min(Math.max(pageOverride, 1), totalPages);

    setPaginationMeta((previous) =>
      calculatePaginationMeta(metaOverride ?? previous, filtered.length, PAGE_SIZE, safePage)
    );
    setFarms(paginateFarms(filtered, safePage, PAGE_SIZE));

    return { filtered, page: safePage, totalPages };
  }, [setFarms, setPaginationMeta]);

  // Read initial page from URL
  const getInitialPage = () => {
    const params = new URLSearchParams(locationObj.search);
    return parseInt(params.get('page'), 10) || 1;
  };
  const getInitialCategory = () => {
    const params = new URLSearchParams(locationObj.search);
    return params.get('category') || categoryId;
  };
  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [currentCategory, setCurrentCategory] = useState(getInitialCategory());

  // Function to search farms by location
  const searchFarmsByLocation = useCallback(async (searchLocation, isFromMap = false, page = 1, options = {}) => {
    const { force = false, bounds = null } = options;
    const activeBounds = bounds || mapBoundsRef.current;

    if (!searchLocation) {
      return;
    }

    const currentCategoryKey = categoryId != null ? String(categoryId) : '';
    const lastCategoryKey = lastSearchCategoryRef.current;

    const isDuplicateLocation =
      !force &&
      lastSearchLocation &&
      Math.abs(lastSearchLocation.lat - searchLocation.lat) < COORDINATE_TOLERANCE &&
      Math.abs(lastSearchLocation.lng - searchLocation.lng) < COORDINATE_TOLERANCE &&
      lastCategoryKey === currentCategoryKey;

    if (isDuplicateLocation) {
      if (isFromMap) {
        setIsMapSearch(true);
      }
      const { page: resolvedPage } = refreshVisibleFarms(allFarmsRef.current, activeBounds, null, page);
      setCurrentPage(resolvedPage);
      return;
    }

    log.log('Searching farms at:', searchLocation, isFromMap ? '(from map)' : '(from location)', `page: ${page}`);
    setLoading(true);
    setIsMapSearch(isFromMap);
    const aggregatedItems = [];
    let aggregatedMeta = {};
    let currentPageIndex = 1;
    let totalPages = 1;

    try {
      while (currentPageIndex <= totalPages) {
        const response = await findFarmsByProximity({
          verbose: VERBOSE,
          latitude: searchLocation.lat,
          longitude: searchLocation.lng,
          distance: DEFAULT_PROXIMITY_DISTANCE,
          categoryId: Number(categoryId),
          page: currentPageIndex,
          limit: API_PAGE_LIMIT,
          sort: SORT,
          order: ORDER
        });

        const pageItems = Array.isArray(response.items) ? response.items : [];
        if (pageItems.length === 0 && currentPageIndex === 1) {
          aggregatedMeta = response.meta || {};
          break;
        }

        aggregatedItems.push(...pageItems);
        aggregatedMeta = response.meta || aggregatedMeta || {};

        const metaPages = Number.isFinite(response.meta?.pages) ? response.meta.pages : null;
        if (metaPages && metaPages > 0) {
          totalPages = metaPages;
        } else if (pageItems.length < API_PAGE_LIMIT) {
          totalPages = currentPageIndex;
        } else {
          totalPages = currentPageIndex + 1;
        }

        if (pageItems.length < API_PAGE_LIMIT) {
          break;
        }

        currentPageIndex += 1;

        if (currentPageIndex > 50) {
          log.warn('Stopping proximity fetch after 50 pages to avoid excessive requests');
          break;
        }
      }

      aggregatedMeta = {
        ...aggregatedMeta,
        total: aggregatedItems.length
      };

      allFarmsRef.current = aggregatedItems;
      const { page: resolvedPage } = refreshVisibleFarms(aggregatedItems, activeBounds, aggregatedMeta, page);
      setLastSearchLocation(searchLocation);
      lastSearchCategoryRef.current = currentCategoryKey;
      setCurrentPage(resolvedPage);
    } catch (error) {
      log.error('Error searching farms:', error);
      allFarmsRef.current = [];
      const { page: resolvedPage } = refreshVisibleFarms([], activeBounds, {}, page);
      setLastSearchLocation(null);
      lastSearchCategoryRef.current = null;
      setCurrentPage(resolvedPage);
    } finally {
      setLoading(false);
    }
  }, [categoryId, lastSearchLocation, refreshVisibleFarms]);

  // Function to handle map bounds change
  const handleMapBoundsChanged = useCallback((mapData) => {
    if (!mapData) {
      return;
    }

    const { center, bounds } = mapData;
    log.log('Map bounds changed:', center);

    if (bounds) {
      mapBoundsRef.current = bounds;
      const { page: resolvedPage } = refreshVisibleFarms(allFarmsRef.current, bounds, { pageOverride: 1 });
      setCurrentPage(resolvedPage);
    }

    searchFarmsByLocation(center, true, 1, { bounds });
  }, [searchFarmsByLocation, refreshVisibleFarms]);

  // Function to handle page change
  const handlePageChange = useCallback((page) => {
    log.log('Page changed to:', page);

  const filtered = Array.isArray(filteredFarmsRef.current) ? filteredFarmsRef.current : [];
  const totalPages = filtered.length > 0 ? Math.ceil(filtered.length / PAGE_SIZE) : 1;
    const safePage = Math.min(Math.max(page, 1), totalPages);

    setCurrentPage(safePage);
    setFarms(paginateFarms(filtered, safePage, PAGE_SIZE));
  setPaginationMeta((prev) => ({ ...prev, page: safePage, pages: totalPages, total: filtered.length, limit: PAGE_SIZE }));

    const params = new URLSearchParams(locationObj.search);
    params.set('page', safePage);

    let categoryName = category?.data?.name || '';
    if (categoryName) {
      categoryName = categoryName.trim().replace(/\s+/g, '-').toLowerCase();
      params.set('category', categoryName);
    }
    navigate(`${locationObj.pathname}?${params.toString()}`, { replace: true });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category, locationObj, navigate, setPaginationMeta]);

  // Prepare markers for Google Map (current page only)
  const markers = (farms || [])
    .filter(farm => farm?.location?.coordinates?.lat && farm?.location?.coordinates?.lon)
    .map(farm => ({
      id: farm.id,
      position: {
        lat: farm.location.coordinates.lat,
        lng: farm.location.coordinates.lon
      },
      label: farm.name,
      location: `${farm.location.address?.city || ''}, ${farm.location.address?.state || ''}`,
      image: farm.coverImage?.viewUrl,
      farm: farm 
 
    }));

  useEffect(() => {
    if (selectedFarm && !farms.some(farm => farm.id === selectedFarm.id)) {
      setSelectedFarm(null);
    }
  }, [farms, selectedFarm]);

  // Toggle map visibility and adjust mobile navigation height
  const handleToggle = () => {
    setAnimClass(showMap ? "slide-up" : "slide-down");
    setTimeout(() => {
      setAnimClass("");
      setShowMap(prev => !prev);
      
      // showMap is true → hide map, false → show map
      if (isMobile) {
        setTimeout(() => {
          const mobileNavContainer = document.querySelector(`.${HeaderStyle["mobile-header-container"]}`);
          if (mobileNavContainer) {
            const isHidden = mobileNavContainer.classList.contains(HeaderStyle["hidden"]);
            if (isHidden) {
              // hidden state, use fixed height
              setMobileNavHeight(85);
            } else {
              const height = mobileNavContainer.offsetHeight;
              setMobileNavHeight(height + 20);
            }
          }
        }, 50);
      }
    }, 100);
  };


  /**
   * UseEffect hooks to handle various side effects:
   * 1. Location changes: Search farms when location is available.
   * 2. Category changes: Fetch category details when categoryId changes.
   * 3. Screen resize: Adjust mobile view and map visibility.
   * 4. Mobile navigation height adjustment.
   * 5. URL synchronization: Sync categoryId from URL if it changes.
   * 6. URL synchronization: Ensure page and category name are always in the URL.
   */  
  
  // 1. Handle location effect - search farms when location changes
  // Track previous location and categoryId to avoid unnecessary resets
  const prevLocationRef = React.useRef();
  const prevCategoryIdRef = React.useRef();
  const prevSourceRef = React.useRef();

  useEffect(() => {
    log.log('FarmList: location:', location, 'locationError:', locationError, 'locationLoading:', locationLoading);

    // If still loading location, wait
    if (locationLoading) {
      log.log("Still loading location...");
      return;
    }

    // Only proceed if we have a valid location
    if (!location) {
      log.warn("No location available after loading completed");
      return;
    }
    const source = location?.source ?? (location?.ip ? 'IP' : 'GPS');
    console.log(`[FarmList][Location] ready from ${source}:`, location);

    if (prevSourceRef.current === 'GPS' && source === 'IP') {
      prevLocationRef.current = location;
      prevCategoryIdRef.current = categoryId;
      prevSourceRef.current = source;
      return;
    }

    // Only reset to page 1 and fetch if location or categoryId actually changed
    const locationChanged =
      prevLocationRef.current?.lat !== location.lat ||
      prevLocationRef.current?.lng !== location.lng;
    const categoryChanged = prevCategoryIdRef.current !== categoryId;

    // If user navigates directly to a page in the URL, use that page
    const params = new URLSearchParams(locationObj.search);
    const urlPage = parseInt(params.get('page'), 10) || 1;
    if ((locationChanged || categoryChanged) && !isMapSearch) {
      setCurrentPage(urlPage);
      searchFarmsByLocation(location, false, urlPage);
    }

    prevLocationRef.current = location;
    prevCategoryIdRef.current = categoryId;
  }, [location, categoryId, locationError, locationLoading, isMapSearch, searchFarmsByLocation, locationObj.search]);

  // 2. Fetch category by ID when categoryId changes
  useEffect(() => {
    if (!categoryId) return;
    setLoading(true);
    setCategory(null);
    getCategoryById(categoryId)
      .then(category => setCategory(category))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [categoryId]);

  // 3. Handle screen resize to toggle mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 787);
      // if the screen is wider than 787px, hide the map
      if (window.innerWidth > 787) {
        setShowMap(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setShowMap]);


  // 4. mobile navigation height adjustment
  useEffect(() => {
    if (isMobile) {
      const measureMobileNavHeight = () => {
        const mobileNavContainer = document.querySelector(`.${HeaderStyle["mobile-header-container"]}`);
        
        if (mobileNavContainer) {
          const isHidden = mobileNavContainer.classList.contains(HeaderStyle["hidden"]);
          if (isHidden) {
            setMobileNavHeight(85);
          } else {
            const height = mobileNavContainer.offsetHeight;
            console.log('Mobile nav container found, height:', height, 'className:', mobileNavContainer.className);
            setMobileNavHeight(height + 20);
          }
        } else {
          console.log('Mobile nav container not found, using fallback'); 
          setMobileNavHeight(85); 
        }
      };

      setTimeout(measureMobileNavHeight, 500);
      
      window.addEventListener('resize', measureMobileNavHeight);
      window.addEventListener('scroll', measureMobileNavHeight, { passive: true });
      
      return () => {
        window.removeEventListener('resize', measureMobileNavHeight);
        window.removeEventListener('scroll', measureMobileNavHeight);
      };
    }
  }, [isMobile, showMap]); 

  // 5. Sync categoryId from URL if it changes
  useEffect(() => {
    const params = new URLSearchParams(locationObj.search);
    const urlCategory = params.get('category');
    if (urlCategory && urlCategory !== currentCategory) {
      setCurrentCategory(urlCategory);
    }
  }, [locationObj.search, currentCategory]);

  // 6. Ensure page and category name are always in the URL
  useEffect(() => {
    const params = new URLSearchParams(locationObj.search);
    let needsUpdate = false;
    if (!params.get('page')) {
      params.set('page', '1');
      needsUpdate = true;
    }
    // Use category name from loaded category data if available
    let categoryName = category?.data?.name || '';
    if (categoryName) {
      categoryName = categoryName.trim().replace(/\s+/g, '-').toLowerCase();
    }
    if (!params.get('category') && categoryName) {
      params.set('category', categoryName);
      needsUpdate = true;
    }
    if (needsUpdate) {
      navigate(`${locationObj.pathname}?${params.toString()}`, { replace: true });
    }
  }, [locationObj, category, navigate]);


  const mobileMapFullScreenMode = isMobile && showMap;

  return (
   <section className={`${FarmListPageStyle["list-section"]}`}>
      <div className={FarmListPageStyle["list-container"]}>
        <div className={FarmListPageStyle["list-main"]}>
          {/* card grid */}
          {/* farm list  */}
          <div className={FarmListPageStyle["list-card-container"]}
            style={
              // Mobile + showMap ON
                isMobile && showMap
                ? { display: "none" }
                : {}
              }
              >
              <h1 className={FarmListPageStyle["list-title"]}>
                {(() => {
                const name = category?.data?.name;
                if (name) {
                  const words = name.trim().split(" ");
                  const lastWord = words[words.length - 1];
                  if (
                  lastWord.toLowerCase().endsWith("farm") &&
                  !lastWord.toLowerCase().endsWith("farms")
                  ) {
                  words[words.length - 1] = lastWord + "s";
                  return words.join(" ");
                  }
                  return name;
                }
                return "Loading Farms...";
                })()}
              </h1>
              <FarmCardList
                farms={farms}
                style={FarmListPageStyle["list-cards"]}
                loading={loading}
                onFarmClick={farm => {
                const slug = slugify(farm.name || '', { lower: true, strict: true });
                navigate(`/farm/${farm.id}/${slug}`);
              }}
              onFarmHover={(farmId) => setHoveredFarmId(farmId)}
              onFarmUnhover={() => setHoveredFarmId(null)}
            />
            
            {/* Pagination */}
            {!loading && paginationMeta && (
              <Pagination
                currentPage={currentPage}
                totalPages={paginationMeta.pages || 1}
                totalItems={paginationMeta.total || 0}
                itemsPerPage={PAGE_SIZE}
                onPageChange={handlePageChange}
                loading={loading}
              />
            )}
          </div>
          {/* end of farm list  */}
          {/* map list  */}
          <div
            className={[
              FarmListPageStyle["list-map-container"],
              mobileMapFullScreenMode && FarmListPageStyle["mobile-map-fullscreen"],
            ]
              .filter(Boolean)
              .join(" ")}
            style={
              isMobile
                ? showMap
                    ? {
                        display: "block",
                      }
                    : { display: "none" }
                : {}
            }
          >
            <div className={FarmListPageStyle["list-map-card"]}>
              <div
                className={[
                  FarmListPageStyle["list-map-img"],
                  mobileMapFullScreenMode && FarmListPageStyle["mobile-map-img-fullscreen"],
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <ListGoogleMapComponent
                  markers={markers}
                  // userLocation={location}                  
                  onLoad={() => {}} // Empty callback since we're not using the map instance
                  onFarmSelect={setSelectedFarm}
                  onMapBoundsChanged={handleMapBoundsChanged} // Add this prop
                  center={(() => {
                    const centerValue = location ? { lat: location.lat, lng: location.lng } : null;
                    return centerValue;
                  })()}
                  highlightFarmId={hoveredFarmId}  
                  mapContainerStyle={{ height: "100%", width: "100%" }}
                />
              </div>
            </div>
          </div>
          {/* end of map list */}
        </div>
      </div>
      {/* Mobile Toggle Button */}
      {isMobile && !selectedFarm && (
          <button
            key={`view-list-btn-${selectedFarm === null}`} 
            className={[
              FarmListPageStyle["toggle-button"],
              animClass && FarmListPageStyle[animClass]
            ].join(" ")}
            style={{
              bottom: isMobile
                ? showMap
                  ? selectedFarm ? '235px' : '25px'
                  : `${mobileNavHeight}px` 
                : '75px'
            }}
            onClick={handleToggle} 
            aria-label={showMap ? "Show List" : "Show Map"}
          >
            <div className={FarmListPageStyle["toggle-button-inner"] }>
              <span className={FarmListPageStyle.toggleButtonLabel}>
                {showMap ? "View List" : "View Map"}
              </span>
            </div>
          </button>
      )}
    </section>
  );
}
